// hooks/useCartStoreCheck.ts
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { CartItem, SaleItemInterface, StockStore } from "../../../types/types";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../../../store/slices/storeListSlice";
import { checkItemAvailability } from "../../../utils/functions/checkItemAvailability";


interface StoreIssue {
  itemId: string;
  itemName: string;
  requested: number;
  available: number;
  shortfall: number;
  willTake: number;
  willLeave: number;
}

interface StoreCheckResult {
  hasIssues: boolean;
  issues: StoreIssue[];
  itemsForOrder: CartItem[];
  itemsRemaining: CartItem[];
  totalShortfall: number;
}

export const useCartStoreCheck = (
  cartItems: CartItem[],
  saleItemList: SaleItemInterface[],
) => {
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);
  const forceStore = currentCluster?.forceStore ?? false;

  const checkStore = useCallback(
    (storeId: number): StoreCheckResult => {
      const issues: StoreIssue[] = [];
      const itemsForOrder: CartItem[] = [];
      const itemsRemaining: CartItem[] = [];

      cartItems.forEach((item) => {
        const saleItem = saleItemList.find((s) => String(s.id) === item.id);

        if (!saleItem) {
          itemsRemaining.push({ ...item });
          return;
        }

        // Если принудительный выбор точки и стоп-лист запрещает товар
        if (forceStore && !saleItem.useStock) {
          const { available } = checkItemAvailability({
            item: saleItem,
            forceStore,
            selectedStoreIds,
          });

          if (!available) {
            itemsRemaining.push({ ...item });
            issues.push({
              itemId: item.id,
              itemName: saleItem.name,
              requested: item.quantity,
              available: 0,
              shortfall: item.quantity,
              willTake: 0,
              willLeave: item.quantity,
            });
            return;
          }
        }

        // Если товар без учёта остатков или forceStore выключен
        if (!saleItem.useStock || !forceStore) {
          itemsForOrder.push({ ...item });
          return;
        }

        // Проверка остатков для useStock = true
        const storeStock = saleItem.stock_stores?.find(
          (s: StockStore) => s.store?.id === storeId,
        );
        const available = storeStock
          ? storeStock.quantity - storeStock.reserved
          : 0;

        if (available >= item.quantity) {
          itemsForOrder.push({ ...item });
        } else if (available > 0) {
          itemsForOrder.push({
            ...item,
            quantity: available,
          });
          itemsRemaining.push({
            ...item,
            quantity: item.quantity - available,
          });
          issues.push({
            itemId: item.id,
            itemName: saleItem.name,
            requested: item.quantity,
            available,
            shortfall: item.quantity - available,
            willTake: available,
            willLeave: item.quantity - available,
          });
        } else {
          itemsRemaining.push({ ...item });
          issues.push({
            itemId: item.id,
            itemName: saleItem.name,
            requested: item.quantity,
            available: 0,
            shortfall: item.quantity,
            willTake: 0,
            willLeave: item.quantity,
          });
        }
      });

      const totalShortfall = issues.reduce((sum, i) => sum + i.shortfall, 0);

      return {
        hasIssues: issues.length > 0,
        issues,
        itemsForOrder,
        itemsRemaining,
        totalShortfall,
      };
    },
    [cartItems, saleItemList, forceStore, selectedStoreIds],
  );

  return { checkStore };
};
